import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Brain, RefreshCw, Image as ImageIcon, X } from 'lucide-react';

export interface SMSReplyComposerProps {
  businessId: string;
  value: string;
  onChange: (value: string) => void;
  onSend: (aiGenerated: boolean, mediaFiles?: File[]) => void;  // Updated to include AI flag and media files
  placeholder?: string;
  disabled?: boolean;
  isSending?: boolean;
  error?: string | null;
  className?: string;
  // AI context props
  conversationId?: string;
  customerId?: string;
  phoneNumber?: string;
  prefetchedSuggestion?: string;
  shouldFetchAI?: boolean; // NEW: Control whether to fetch AI suggestions
  onAIStateChange?: (isLoading: boolean, error: string | null, isAiText: boolean) => void; // NEW: Callback for AI state changes
  onRefreshAI?: (refreshHandler: () => void) => void; // NEW: Callback to expose AI refresh handler
}

export function SMSReplyComposer({
  businessId,
  value,
  onChange,
  onSend,
  placeholder = 'Type your SMS message here...',
  disabled = false,
  isSending = false,
  error = null,
  className = '',
  conversationId,
  customerId,
  phoneNumber,
  prefetchedSuggestion,
  shouldFetchAI = true, // Default to true for backward compatibility
  onAIStateChange, // New prop
  onRefreshAI // New prop
}: SMSReplyComposerProps) {
  const [isAiText, setIsAiText] = useState(false);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<Map<number, string>>(new Map());
  const [localValue, setLocalValue] = useState(value); // Local state for fast typing
  
  // Refs for request management
  const currentRequestRef = useRef<AbortController | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastRequestKeyRef = useRef<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const onChangeDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-resize textarea based on content
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';
    
    // Calculate new height (with a max height)
    const newHeight = Math.min(textarea.scrollHeight, 160); // Matches max-h-40
    textarea.style.height = `${newHeight}px`;
  }, []);

  // Adjust height whenever value changes
  useEffect(() => {
    adjustTextareaHeight();
  }, [localValue, adjustTextareaHeight]);

  // Create a debounced fetch function
  const debouncedFetchAISuggestion = useCallback(async () => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Create a unique key for this request
    const requestKey = `${businessId}-${conversationId}-${customerId}-${phoneNumber}`;
    
    // Don't make the same request twice
    if (requestKey === lastRequestKeyRef.current && !hasUserInteracted) {
      return;
    }

    timeoutRef.current = setTimeout(async () => {
      if (!businessId || isLoadingAI || hasUserInteracted || localValue) return;

      // Cancel any existing request
      if (currentRequestRef.current) {
        currentRequestRef.current.abort();
      }

      // Create new abort controller
      currentRequestRef.current = new AbortController();
      lastRequestKeyRef.current = requestKey;
      
      setIsLoadingAI(true);
      setAiError(null);

      try {
        const response = await fetch(`${process.env.VITE_AI_SERVICE_URL || 'https://booking-app-production-d357.up.railway.app'}/generate-replies`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            business_id: businessId,
            conversation_id: conversationId,
            customer_id: customerId,
            phone_number: phoneNumber,
            message_type: 'sms',
            recent_messages_count: 10
          }),
          signal: currentRequestRef.current.signal
        });

        if (!response.ok) {
          throw new Error(`Failed to generate suggestions: ${response.status}`);
        }

        const data = await response.json();
        if (data.suggestions && data.suggestions.length > 0) {
          // Use the first suggestion (usually professional tone)
          const suggestion = data.suggestions[0].text;
          if (!hasUserInteracted && !localValue) {
            setLocalValue(suggestion);
            onChange(suggestion);
            setIsAiText(true);
          }
        }
      } catch (err) {
        // Don't show error if request was aborted
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        console.error('Error fetching AI suggestion:', err);
        setAiError(err instanceof Error ? err.message : 'Failed to get AI suggestion');
      } finally {
        setIsLoadingAI(false);
        currentRequestRef.current = null;
      }
      }, 300); // 300ms debounce
  }, [businessId, conversationId, customerId, phoneNumber, hasUserInteracted, localValue, isLoadingAI]);

  // Sync local value to external value when prop changes
  useEffect(() => {
    if (value !== localValue) {
      setLocalValue(value);
    }
  }, [value]);

  // Use prefetched suggestion when available
  useEffect(() => {
    if (prefetchedSuggestion && !hasUserInteracted && !localValue) {
      setLocalValue(prefetchedSuggestion);
      onChange(prefetchedSuggestion);
      setIsAiText(true);
      return;
    }
  }, [prefetchedSuggestion, hasUserInteracted, localValue, onChange]);


  // Auto-fetch AI suggestion when component mounts or context changes (only if enabled and no prefetched suggestion)
  useEffect(() => {
    if (shouldFetchAI && businessId && !hasUserInteracted && !localValue && !prefetchedSuggestion) {
      console.log('🤖 SMSReplyComposer: Auto-fetching AI suggestions');
      debouncedFetchAISuggestion();
    } else if (!shouldFetchAI) {
      console.log('🤖 SMSReplyComposer: AI suggestions disabled');
    }

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (currentRequestRef.current) {
        currentRequestRef.current.abort();
      }
    };
  }, [shouldFetchAI, businessId, conversationId, customerId, phoneNumber, prefetchedSuggestion]); // Fixed: Don't depend on the function itself

  const handleInputChange = (newValue: string) => {
    setHasUserInteracted(true);
    setIsAiText(false);
    
    // Update local state immediately for fast UI response
    setLocalValue(newValue);
    
    // Debounce the parent onChange to reduce re-renders
    if (onChangeDebounceRef.current) {
      clearTimeout(onChangeDebounceRef.current);
    }
    
    onChangeDebounceRef.current = setTimeout(() => {
      onChange(newValue);
    }, 100); // Only sync to parent every 100ms
  };

  const handleSend = () => {
    // Flush any pending onChange calls
    if (onChangeDebounceRef.current) {
      clearTimeout(onChangeDebounceRef.current);
      onChangeDebounceRef.current = null;
    }
    
    // Sync latest value to parent before sending
    onChange(localValue);
    
    // Only send if there's content or files
    if (!localValue.trim() && attachedFiles.length === 0) {
      return;
    }
    
    onSend(isAiText, attachedFiles.length > 0 ? attachedFiles : undefined);  // Pass the AI flag and media files to the parent
    
    // Clear attached files after sending
    setAttachedFiles([]);
    setFilePreviews(new Map());
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Filter to only image files (MMS supports images). iOS WKWebView (Capacitor)
    // camera capture frequently returns a File with an EMPTY mime type, so a plain
    // `file.type.startsWith('image/')` check silently drops camera photos. Accept
    // empty-type / image-extension files and re-wrap them with a valid type+name so
    // the downstream MMS upload sets a correct content type.
    const IMAGE_EXT_RE = /\.(jpe?g|png|gif|heic|heif|webp|bmp|tiff?)$/i;
    const imageFiles = files
      .map((file) => {
        const isImageType = file.type.startsWith('image/');
        const looksLikeImage = !file.type || IMAGE_EXT_RE.test(file.name || '');
        if (!isImageType && !looksLikeImage) return null;
        if (!file.type) {
          const safeName = file.name && file.name.trim() ? file.name : `photo_${Date.now()}.jpg`;
          return new File([file], safeName, { type: 'image/jpeg', lastModified: file.lastModified });
        }
        return file;
      })
      .filter((file): file is File => file !== null);

    if (imageFiles.length === 0) {
      alert('Please select image files only. MMS supports images.');
      return;
    }

    // Limit to 5 images (Twilio MMS limit)
    const filesToAdd = imageFiles.slice(0, 5 - attachedFiles.length);
    
    setAttachedFiles(prev => [...prev, ...filesToAdd]);

    // Create previews for the new files
    filesToAdd.forEach((file, index) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const previewUrl = e.target?.result as string;
        setFilePreviews(prev => {
          const newMap = new Map(prev);
          newMap.set(attachedFiles.length + index, previewUrl);
          return newMap;
        });
      };
      reader.readAsDataURL(file);
    });

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    setAttachedFiles(prev => {
      const newFiles = prev.filter((_, i) => i !== index);
      // Reindex previews to match new file array
      setFilePreviews(prevPreviews => {
        const newPreviews = new Map();
        prev.forEach((_, i) => {
          if (i !== index) {
            const newIndex = i > index ? i - 1 : i;
            const preview = prevPreviews.get(i);
            if (preview) {
              newPreviews.set(newIndex, preview);
            }
          }
        });
        return newPreviews;
      });
      return newFiles;
    });
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    if (isAiText) {
      // Clear the AI text when user focuses to start typing their own message
      setIsAiText(false);
      setHasUserInteracted(true);
      // Keep the text but mark it as user text now
    }
    // Stop WKWebView from slowly scrolling the huge conversation tree to reveal
    // the field — keyboard inset CSS handles lifting the composer instead.
    try {
      e.target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    } catch {
      // ignore
    }
  };

  const handleInputClick = () => {
    if (isAiText) {
      setHasUserInteracted(true);
    }
  };

  // Removed handleKeyDown - Enter key now creates new lines, only send button sends

  const handleRefreshAI = async () => {
    // Cancel any existing request
    if (currentRequestRef.current) {
      currentRequestRef.current.abort();
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Reset states
    setHasUserInteracted(false);
    setIsAiText(false);
    setAiError(null);
    onChange('');
    
    // Create new abort controller
    currentRequestRef.current = new AbortController();
    setIsLoadingAI(true);

    try {
      const response = await fetch(`${process.env.VITE_AI_SERVICE_URL || 'https://booking-app-production-d357.up.railway.app'}/generate-replies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          business_id: businessId,
          conversation_id: conversationId,
          customer_id: customerId,
          phone_number: phoneNumber,
          message_type: 'sms',
          recent_messages_count: 10
        }),
        signal: currentRequestRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`Failed to generate suggestions: ${response.status}`);
      }

      const data = await response.json();
      if (data.suggestions && data.suggestions.length > 0) {
        const suggestion = data.suggestions[0].text;
        onChange(suggestion);
        setIsAiText(true);
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      console.error('Error refreshing AI suggestion:', err);
      setAiError(err instanceof Error ? err.message : 'Failed to get AI suggestion');
    } finally {
      setIsLoadingAI(false);
      currentRequestRef.current = null;
    }
  };

  // Call the new prop callback when AI state changes
  useEffect(() => {
    if (onAIStateChange) {
      onAIStateChange(isLoadingAI, aiError, isAiText);
    }
  }, [isLoadingAI, aiError, isAiText, onAIStateChange]);

  // Create a ref to expose the refresh function
  const refreshAIRef = useRef(handleRefreshAI);
  
  // Update ref when handler changes
  useEffect(() => {
    refreshAIRef.current = handleRefreshAI;
  }, [handleRefreshAI]);

  // Call onRefreshAI prop with the current refresh function
  useEffect(() => {
    if (onRefreshAI) {
      onRefreshAI(refreshAIRef.current);
    }
  }, [onRefreshAI]);

  return (
    <div className={className}>
      {/* AI Status Indicator - Moved to toolbar, only show if onAIStateChange not provided (backward compatibility) */}
      {!onAIStateChange && (
        <div className="flex items-center space-x-3 text-[10px] sm:text-xs py-1 min-h-[20px]">
          {(isLoadingAI || aiError || (isAiText && localValue)) && (
            <>
              <div className="flex items-center space-x-2">
                {isLoadingAI ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-2 border-indigo-600 border-t-transparent"></div>
                    <span className="text-indigo-600">Generating AI suggestion...</span>
                  </>
                ) : aiError ? (
                  <span className="text-red-500">AI suggestion failed</span>
                ) : isAiText ? (
                  <>
                    <Brain className="h-3 w-3 text-indigo-600" />
                    <span className="text-indigo-600">AI suggested reply</span>
                  </>
                ) : null}
              </div>
              
              {!isLoadingAI && (
                <button
                  onClick={handleRefreshAI}
                  className="text-indigo-600 hover:text-indigo-800 transition-colors"
                  title="Get new AI suggestion"
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Attached Files Preview */}
      {attachedFiles.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {attachedFiles.map((file, index) => {
            const preview = filePreviews.get(index);
            return (
              <div key={index} className="flex max-w-full items-center gap-2 rounded-xl border border-zinc-200 bg-white py-1 pl-1 pr-2 text-[13px] text-zinc-800 shadow-sm">
                {preview ? (
                  <img src={preview} alt={file.name} className="h-10 w-10 rounded-lg object-cover" />
                ) : (
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-zinc-100 text-zinc-500">
                    <ImageIcon className="h-4 w-4" />
                  </span>
                )}
                <span className="max-w-[160px] truncate">{file.name}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveFile(index)}
                  aria-label={`Remove ${file.name}`}
                  className="shrink-0 text-zinc-400 hover:text-zinc-700"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* SMS input — same pill as the agent chat composer */}
      <div className={`flex items-end gap-1 rounded-full border border-zinc-200 bg-white py-1.5 pl-1.5 pr-1.5 shadow-sm ${disabled || isSending ? 'opacity-60' : ''}`}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled || isSending || attachedFiles.length >= 5}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || isSending || attachedFiles.length >= 5}
          className="mb-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-100 disabled:opacity-40"
          aria-label="Attach photo"
          title={attachedFiles.length >= 5 ? 'Maximum 5 photos' : 'Attach photo'}
        >
          <ImageIcon className="h-5 w-5" />
        </button>
        <textarea
          ref={textareaRef}
          placeholder={isLoadingAI ? 'Getting AI suggestion...' : placeholder}
          className={`max-h-40 min-h-[36px] w-full flex-1 resize-none overflow-y-auto bg-transparent py-2 text-base leading-6 outline-none placeholder:text-zinc-400 md:text-[15px] ${
            isAiText ? 'italic text-zinc-400' : 'text-zinc-900'
          }`}
          value={localValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleInputFocus}
          onClick={handleInputClick}
          onBlur={() => {
            // Flush pending onChange on blur
            if (onChangeDebounceRef.current) {
              clearTimeout(onChangeDebounceRef.current);
              onChangeDebounceRef.current = null;
              onChange(localValue);
            }
          }}
          disabled={disabled || isSending}
          rows={1}
        />
        <button
          type="button"
          disabled={disabled || isSending || (!localValue.trim() && attachedFiles.length === 0)}
          onClick={handleSend}
          className="mb-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-neutral-950 text-white transition active:scale-95 disabled:opacity-40"
          aria-label="Send SMS"
          title={isSending ? 'Sending...' : isAiText ? 'Send AI suggestion' : 'Send SMS'}
        >
          {isSending ? (
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mt-1.5 text-[12px] text-rose-600">
          {error}
        </div>
      )}


    </div>
  );
} 